/**
 * The UI string catalogue. This file is the source of truth: every string the
 * app renders has an English default here, and a Prismic `language` document
 * may override any of them key by key.
 *
 * The groups mirror the tabs of the `language` custom type, and each key is
 * the API id of the matching Key Text field, so a translator sees the same
 * names an implementer does. `customtypes/language/index.json` is generated
 * from this object (`yarn generate:language-type`) and a test asserts the two
 * never drift.
 *
 * Keys are unique across every group because a Prismic document's data is flat.
 */
export const catalogue = {
  onboarding: {
    welcome_title: 'Stay safe at the beach',
    welcome_intro: 'Beach Passport is a short virtual course designed to help you stay safe at the beach.',
    welcome_start: 'Start',
    welcome_trust: 'No account needed. Your progress saves on this device.',
    /* The label over the welcome screen's language picker. Deliberately not
       `language_title`: that is a question heading on a screen of its own, and
       this names a field sitting between two other blocks. */
    welcome_language_label: 'Language',
    step_progress: 'Step {step} of {total}',
    language_title: 'Please select your language',
    language_subtitle: 'Tap your language to switch the whole course.',
    audience_group_title: 'Select your age group',
    audience_subtitle: 'This sets which version of the course you get.',
    audience_child: 'Primary school',
    audience_teen: 'High school',
    audience_adult: 'Adult (18 or over)',
    profile_title: 'Enter your name',
    profile_name_label: 'Your name',
    profile_name_placeholder: 'e.g. Sam',
    profile_buddy_label: 'Choose your character',
    /* The course picker in settings. */
    profile_audience_label: 'Your course',
    profile_buddy_kangaroo: 'Pounce',
    profile_buddy_koala: 'Frizzy',
    /* The Kangaroo Beach junior cadets, by name. The keys (and the stored
       buddy values) say duck and squirrel for historical reasons; Gemma is a
       platypus and Neville a wombat. */
    profile_buddy_duck: 'Gemma',
    profile_buddy_squirrel: 'Neville',
    profile_continue: 'Continue',
    profile_save: 'Save changes',
    /* Above the partner logo on the age and name screens. */
    partner_label: 'In partnership with',

    /* Browser/tab titles. React Router builds them through `metaFor`, so they
       are translated like anything else on the screen they name. */
    meta_start: 'Get started',
    meta_profile: 'Your details',
  },

  header: {
    header_progress_label: 'Your progress',
    header_language_label: 'Language',
    header_menu_label: 'Menu',
    header_menu_close: 'Close menu',
    header_progress_count: '{done} of {total}',
    menu_journey: 'Modules',
    menu_settings: 'Settings',
    menu_about: 'About',
    menu_resources: 'Resources',
    menu_contact: 'Contact us',
    menu_privacy: 'Privacy',
    menu_terms: 'Terms of use',
    menu_start_over: 'Start over',
    header_close: 'Close',
    language_sheet_title: 'Change language',
    language_sheet_note: 'Your progress comes with you, even halfway through a module.',
    start_over_title: 'Start over?',
    start_over_body: 'This clears your name and every module you have finished. It cannot be undone.',
    start_over_cancel: 'Keep my progress',
    start_over_confirm: 'Erase everything and start over',
  },

  journey: {
    journey_greeting: 'Hi',
    journey_subtitle: 'Complete all {total} modules to earn your Beach Passport.',
    journey_module_done: 'Done',
    journey_module_next: 'Up next',
    journey_module_todo: 'To do',
    journey_module_in_progress: 'In progress',
    journey_module_progress: '{percent}% done',
    journey_module_duration: '{minutes} min',
    journey_module_label: 'Module {position}',
    /** Screen-reader label for the stars on a finished module. */
    journey_module_score: '{score} out of 5 stars',
    journey_start_module: 'Start',
    /* The bottom bar's action when the recommended module is part-way done. */
    journey_continue_module: 'Continue',
    journey_resume_module: 'Start again',
    journey_reward_reminder: 'Answer correctly to boost your star rating.',
    journey_all_done_title: 'Every module done',
    journey_all_done_body: 'Nice work — your Beach Passport is ready to claim.',
    journey_get_passport: 'Get my passport',
    journey_empty: 'No modules are published for this language and age group yet.',
    meta_journey: 'Your modules',
  },

  chat: {
    chat_module_label: 'Module {current} of {total}',
    chat_back: 'Back to modules',
    /* The footer while the coach is typing. */
    chat_coach_typing: 'One moment…',
    chat_quick_continue: 'Got it',
    chat_check_answer: 'Check answer',
    chat_check_answer_count: 'Check answer ({count})',
    chat_reset: 'Reset',
    chat_try_again: 'Have another go',
    chat_correct: 'That’s right',
    chat_incorrect: 'Not quite',
    /* A second or later wrong answer on an activity. */
    chat_incorrect_hint: 'Not quite. Have a look at the hint on the card and try again.',
    chat_attempt_count: 'Attempt {current}',
    chat_reveal: 'Here’s the answer',
    /* Offered after three wrong answers on an activity. */
    chat_show_answer: 'Show me the answer',
    chat_send: 'Send',
    chat_text_placeholder: 'Type your answer',
    chat_transcript_show: 'Show transcript',
    chat_transcript_hide: 'Hide transcript',
    chat_play_video: 'Play video',
    chat_video_gate_hint: 'Watch the video, then continue',
    chat_video_watch: 'Watch the video',
    chat_video_continue: 'Continue',
    // Spoken as the player dialog's description, so a screen reader learns the
    // transcript is there without having to go looking for it.
    chat_transcript_available: 'A transcript is available under the player.',
    chat_image_close: 'Close',
    chat_takeaways_heading: 'Key takeaways',
    /* Shown above the coach's messages and read as the avatar's name. */
    chat_coach_name: 'Surf Lifesaver',
    chat_you: 'You',
    chat_coach_is_typing: '{name} is typing',
    chat_pick_one: 'Pick one',
    // The accessible name of a picture answer whose asset has no alt text.
    chat_option_number: 'Option {number}',
    chat_pick_all: 'Pick all that apply',
    chat_checkpoint_label: 'Checkpoint {current} of {total}',
    chat_module_progress: '{percent}% of this module done',
    chat_minutes_left: '~{minutes} min left',
    chat_image_open: 'Tap to look closer',
    chat_image_of: 'Image {current} of {total}',
    /* Opens the activity picture full screen. */
    chat_activity_expand: 'Make the picture bigger',
    chat_image_previous: 'Previous image',
    chat_image_next: 'Next image',
    chat_transcript_heading: 'Transcript',
    chat_scroll_latest: 'Jump to the latest message',
    chat_scroll_earliest: 'Jump to the first message',
    chat_video_unavailable: 'This video isn’t available yet.',
    chat_empty: '[empty]',

    // Discovery sets: the fallback instruction when an editor left the slice's
    // own prompt blank, and the counter beside it.
    chat_discover_heading: 'Pick one to explore',
    chat_discover_remaining: '{done} of {total}',

    // Activity cards (ticket 07).
    chat_activity_correct: 'Correct',
    chat_activity_shown: 'Answer shown',
    chat_activity_elsewhere: 'somewhere else on the photo',
    chat_activity_pin_placed: 'Pin placed on {label}',
    chat_activity_pin_cleared: 'Pin cleared',
    chat_activity_region: 'Region {current} of {total} of the picture',
    chat_activity_tray: 'Drag these on',
    /* Both flags move together as one marker. */
    chat_activity_markers: 'The markers, still to place',
    chat_activity_markers_placed: 'The markers are on {label}',
    chat_activity_target: 'Put the markers on {label}',
    /* Activity hints from the third attempt. An area on the picture, and the
       first step of an ordering. */
    chat_activity_hint_area: 'Somewhere in here',
    chat_activity_hint_first: 'Start here',
    chat_activity_step_picked: 'Step {current}: {label}',
    chat_activity_steps_cleared: 'Order cleared',
    chat_activity_answer_order: 'The right order',
    chat_activity_your_answer: 'Your answer',
  },

  completion: {
    completion_title: 'Module complete',
    completion_body: 'Nice work. That module is ticked off.',
    /* The completion card after the last module. */
    completion_course_title: 'Course complete',
    completion_course_body: 'That’s every module. Your Beach Passport is ready to claim.',
    completion_get_passport: 'Get my passport',
    completion_up_next: 'Up next: {title}',
    completion_continue: 'Next',
    completion_score: '{score} out of 5',
    completion_stars_label: '{score} out of 5 stars',
  },

  passport: {
    passport_earned_title: 'You’ve earned your Beach Passport',
    /*
      Deliberately says nothing about a recap: the recap section only renders
      when a topic has authored `key_takeaways`, and almost none in production
      have, so "here's what you learnt" was a promise the screen then broke.
    */
    passport_earned_body: 'All {total} modules done. Your Beach Passport is ready to claim.',
    passport_recap_heading: 'What you learnt',
    passport_get: 'Get my Beach Passport',
    passport_form_intro: 'We only need a few details so your Beach Passport reads correctly.',
    passport_generating: 'Making your Beach Passport…',
    passport_error_body: 'We couldn’t make your passport just then. Please try again.',
    passport_retry: 'Try again',
    passport_ready_title: 'Your Beach Passport is ready',
    passport_certificate_alt: 'Your Beach Passport',
    passport_download: 'Download my Beach Passport',
    passport_share: 'Share',
    passport_share_copied: 'Link copied',
    passport_links_title: 'Keep going',
    passport_links_body: 'A few more ways to stay safe at the beach and get involved.',
    passport_back_to_journey: 'Back to my modules',
    passport_resources: 'More beach safety resources',
    meta_get_passport: 'Get my Beach Passport',
    meta_passport: 'Your Beach Passport',
    meta_passport_ready: 'Your Beach Passport is ready',

    /*
      The built-in partner links on the certificate page
      (`components/passport/certificate-links.tsx`), shown when the language
      document has no Passport links. Their URLs and artwork stay
      in code; only the words are here. They name other organisations'
      programmes, so a translator should render the call to action, not the
      proper noun: `Nippers`, `Kangaroo Beach`, `ABC iview`, `SLSA` and
      `AUSTSWIM` are brands.
    */
    /* The button on a Passport link with a blank label. */
    passport_link_learn_more: 'Find out more',
    passport_link_nippers_title: 'Become a Nipper',
    passport_link_nippers_body: 'Join the fun and learn beach safety skills with other kids your age.',
    passport_link_nippers_cta: 'Join Nippers',
    passport_link_kangaroo_beach_title: 'Kangaroo Beach',
    passport_link_kangaroo_beach_body: 'Watch beach adventures and pick up safety tips with your favourite characters.',
    passport_link_kangaroo_beach_cta: 'Watch now',
    passport_link_iview_title: 'Watch on ABC iview',
    passport_link_iview_body: 'Stream Kangaroo Beach episodes any time on ABC iview.',
    passport_link_iview_cta: 'Stream free',
    passport_link_get_involved_title: 'Get involved',
    passport_link_get_involved_body: 'Join Surf Life Saving and help keep our beaches safe for everyone.',
    passport_link_get_involved_cta: 'Find out more',
    passport_link_find_a_beach_title: 'Find a beach',
    passport_link_find_a_beach_body: 'Check beach conditions, hazards and safety information before you visit.',
    passport_link_find_a_beach_cta: 'Check a beach',
    passport_link_support_title: 'Support SLSA',
    passport_link_support_body: 'Help us continue our lifesaving work and beach safety education programs.',
    passport_link_support_cta: 'Learn more',
    passport_link_learn_to_swim_title: 'Learn to swim',
    passport_link_learn_to_swim_body: 'Find swimming lessons near you and build confidence in the water.',
    passport_link_learn_to_swim_cta: 'Find lessons',
  },

  content: {
    content_back: 'Back',
    content_skip_to_content: 'Skip to content',
    content_loading: 'Loading',
    content_new_tab: 'Opens in a new tab',
    content_form_unavailable:
      'The contact form is not available right now. Please use the contact details above to get in touch.',
    content_contact: 'Contact us',
    footer_copyright: '© Surf Life Saving Australia',
    footer_privacy: 'Privacy',
    footer_terms: 'Terms of use',
    footer_contact: 'Contact us',
    footer_nav_label: 'Site information',
    /* The agency credit under the welcome trust line and in the footer. The
       April9 wordmark follows it, so this is the lead-in and not a sentence. */
    footer_developed_by: 'Developed by',
  },

  errors: {
    error_name_required: 'Please enter your name.',
    error_name_invalid: 'Please enter your name using letters only, up to 20 characters.',
    error_generic_title: 'Something went wrong',
    error_generic_body: 'Sorry, that didn’t work. Please try again.',
    error_not_found_title: 'Page not found',
    error_not_found_body: 'That page doesn’t exist. Head back to the start.',
    error_retry: 'Try again',
    error_reload: 'Reload the page',
    error_home: 'Go back home',
    error_offline_title: 'You’re offline',
    error_offline_body: 'Check your internet connection and try again.',
    error_server_title: 'Server error',
    error_server_body: 'Something went wrong on our end. Please try again.',
    error_unauthorised_title: 'Not allowed',
    error_unauthorised_body: 'You don’t have access to that page.',
  },
} as const;

/** The tab/group names of the catalogue, in the order they appear in Prismic. */
export type StringGroup = keyof typeof catalogue;

/** Every string key, flattened — also the API id of its Prismic field. */
export type StringKey = { [G in StringGroup]: keyof (typeof catalogue)[G] }[StringGroup];

/** The catalogue shape with widened values: what `resolveStrings` returns. */
export type Strings = { [G in StringGroup]: { [K in keyof (typeof catalogue)[G]]: string } };

/**
 * The subset of a `language` document's data that overrides strings. Typed
 * loosely on purpose: a `LanguageDocumentData` carries the picker fields too,
 * and an unfilled Key Text field is `null`.
 */
export type StringOverrides = Partial<Record<StringKey, string | null | undefined>>;

const groups = Object.keys(catalogue) as Array<StringGroup>;

/**
 * Merges a `language` document's strings over the English defaults. An absent,
 * empty or whitespace-only override falls back to English — a string is never
 * hidden, so a half-translated language is still usable.
 */
export function resolveStrings(overrides: StringOverrides | null | undefined): Strings {
  const resolved = {} as { [G in StringGroup]: Record<string, string> };

  for (const group of groups) {
    const defaults: Record<string, string> = catalogue[group];
    const merged: Record<string, string> = {};

    for (const [key, fallback] of Object.entries(defaults)) {
      const override = overrides?.[key as StringKey];
      merged[key] = typeof override === 'string' && override.trim() !== '' ? override : fallback;
    }

    resolved[group] = merged;
  }

  return resolved as Strings;
}

/** The English defaults, ready to serve when no `language` document exists. */
export const englishStrings = resolveStrings(null);

/**
 * Fills `{placeholder}` tokens in a catalogue string. Values are stringified as
 * they are, so a caller can pass numbers straight through.
 */
export function formatString(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, token: string) => (token in values ? String(values[token]) : match));
}
